import { format } from "date-fns";
import { Link } from "expo-router";
import { ScrollView, View } from "react-native";
import Markdown from "react-native-markdown-display";
import { Avatar, Card, Icon, Text, useTheme } from "react-native-paper";
import { useTailwind } from "tailwind-rn";
import { Carriers, CuantoLogoDark, useMediaQuery } from "ui";
import { ProgressBar } from "ui/delivery";

import { OrderDetails } from "./details";
import ActionButton from "@/src/components/action-button";
import Disclaimer from "@/src/components/disclaimer";
import Eta from "@/src/components/eta";
import { SubstatusIcon } from "@/src/components/substatus-icon";
import SubstatusLabel from "@/src/components/substatus-label";
import Title from "@/src/components/typography/title";
import useDeliveryStatus from "@/src/contexts/delivery-status-context";
import { Status } from "@/src/types";

export default function StatusScreen() {
  const tailwind = useTailwind();
  const { greaterThan } = useMediaQuery();

  return (
    <ScrollView>
      <View style={tailwind("p-4 gap-4")}>
        <Header />
        <View style={tailwind("flex-row gap-4 flex-1")}>
          <View style={tailwind("gap-4 flex-1")}>
            <Title text="¿Dónde está mi pedido?" />
            <TrackingStatusCard />
            <TrackingSubstatusCard />
            <TrackingHistoryCard />
          </View>
          {greaterThan("lg") && (
            <View style={tailwind("gap-4 flex-1")}>
              <Title text="Detalles del pedido" />
              <OrderDetails />
            </View>
          )}
        </View>
        <Disclaimer />
      </View>
    </ScrollView>
  );
}

export function TrackingHistoryCard() {
  const tailwind = useTailwind();
  const theme = useTheme();

  const { deliveryStatus } = useDeliveryStatus();

  if (!deliveryStatus) return null;

  return (
    <Card style={tailwind("rounded-xl")}>
      <Card.Content style={tailwind("gap-2")}>
        <Text variant="titleMedium">Historial</Text>
        <View>
          {deliveryStatus.tracking_history.map((event, index) => (
            <View key={index} style={tailwind("flex-row gap-4")}>
              <View style={tailwind("items-center")}>
                <Avatar.Icon
                  icon="check"
                  size={40}
                  style={{ backgroundColor: theme.colors.surfaceVariant }}
                  color={theme.colors.onSurfaceVariant}
                />
                {index < deliveryStatus.tracking_history.length - 1 ? (
                  <View
                    style={[
                      tailwind("h-8 w-0.5 my-0.5"),
                      { backgroundColor: theme.colors.onPrimaryContainer },
                    ]}
                  />
                ) : null}
              </View>
              <View style={tailwind("flex-1")}>
                <Text variant="titleMedium">{event.label}</Text>
                <Text variant="bodyMedium">
                  {format(event.created_at, "d MMM y h:mm aaa")}
                </Text>
              </View>
            </View>
          ))}
        </View>
      </Card.Content>
    </Card>
  );
}

function TrackingStatusCard() {
  const tailwind = useTailwind();

  const { deliveryStatus } = useDeliveryStatus();
  if (!deliveryStatus) return null;

  const { eta, carrier, tracking_status } = deliveryStatus;
  const { status, substatus } = tracking_status;

  return (
    <Card style={tailwind("rounded-xl")}>
      <Card.Content style={tailwind("gap-2")}>
        {eta ? (
          <>
            <Text variant="titleMedium">Entrega estimada</Text>
            <Eta eta={eta} />
          </>
        ) : (
          <SubstatusLabel substatus={substatus} />
        )}
        <ProgressBar status={status} substatus={substatus} carrier={carrier} />
      </Card.Content>
    </Card>
  );
}

export function TrackingSubstatusCard() {
  const tailwind = useTailwind();

  const { deliveryStatus } = useDeliveryStatus();
  if (!deliveryStatus) return null;

  return (
    <Card style={tailwind("rounded-xl")}>
      <Card.Content style={tailwind("gap-2")}>
        <Text variant="titleMedium">Estado del pedido</Text>

        <View style={tailwind("flex-row gap-2")}>
          <SubstatusIcon
            substatus={deliveryStatus.tracking_status.substatus}
            paymentMethod={deliveryStatus.order.payment_method}
          />
          <View style={tailwind("flex-1")}>
            <Text variant="titleMedium">
              {deliveryStatus.tracking_status.label}
            </Text>
            <Text variant="bodySmall">
              <Markdown style={{ paragraph: tailwind("m-0") }}>
                {deliveryStatus.tracking_status.details}
              </Markdown>
            </Text>
            <CarrierInfo />
          </View>
        </View>

        {deliveryStatus.tracking_status.actions.length ? (
          <View style={tailwind("flex-row self-end gap-2")}>
            {deliveryStatus.tracking_status.actions.map((action, index) => (
              <ActionButton action={action} key={index} />
            ))}
          </View>
        ) : null}
      </Card.Content>
    </Card>
  );
}

function CarrierInfo() {
  const tailwind = useTailwind();

  const { deliveryStatus } = useDeliveryStatus();

  const statusesToShowCarrier: Status[] = ["pre_transit", "transit", "failure"];

  if (
    !deliveryStatus ||
    !statusesToShowCarrier.includes(deliveryStatus.tracking_status.status)
  ) {
    return null;
  }

  function getCarrier() {
    if (!deliveryStatus?.carrier) return null;

    switch (deliveryStatus.carrier) {
      case "merchant":
        return (
          <View style={tailwind("flex-row items-center gap-1 pl-1")}>
            <Avatar.Image
              source={{ uri: deliveryStatus.order.merchant.avatar_url }}
              size={16}
            />
            <Text>{deliveryStatus.order.merchant.name}</Text>
          </View>
        );
      default:
        return (
          <Carriers.Logotype carrier={deliveryStatus.carrier} height={20} />
        );
    }
  }

  return (
    <View style={tailwind("flex-row gap-1 pt-2")}>
      <Text>a cargo de</Text>
      {getCarrier()}
    </View>
  );
}

function Header() {
  const tailwind = useTailwind();
  const { greaterThan } = useMediaQuery();
  const isLarge = greaterThan("lg");

  return (
    <View style={tailwind(isLarge ? "gap-3 flex-row items-center" : "gap-4")}>
      <CuantoLogoDark height={32} />
      {isLarge && <View style={tailwind("bg-primary-500 h-10 w-0.5")} />}
      <OrderInfo />
    </View>
  );
}

function MerchantInfo() {
  const tailwind = useTailwind();
  const { colors } = useTheme();

  const { deliveryStatus } = useDeliveryStatus();
  if (!deliveryStatus) return null;

  const { merchant, simple_id } = deliveryStatus.order;

  return (
    <View style={tailwind("flex-row items-center gap-2 flex-1")}>
      <Avatar.Image source={{ uri: merchant.avatar_url }} size={50} />
      <View style={tailwind("flex-1")}>
        <Text variant="labelLarge" style={{ color: colors.onSurfaceVariant }}>
          {merchant.name}
        </Text>

        {simple_id && <OutlineText text={`Pedido #${simple_id}`} />}
      </View>
    </View>
  );
}

function OrderInfo() {
  const tailwind = useTailwind();
  const { deliveryStatus } = useDeliveryStatus();
  const theme = useTheme();
  const { greaterThan } = useMediaQuery();

  if (!deliveryStatus) return null;

  return (
    <View style={tailwind("flex-row items-center gap-2")}>
      <MerchantInfo />

      {greaterThan("lg") ? null : (
        <Link
          asChild
          href={{
            pathname: "/[uuid]/details",
            params: { uuid: deliveryStatus.order.uuid },
          }}
        >
          <View style={tailwind("gap-2 flex-row items-center")}>
            <OutlineText text="ver detalle" />
            <Icon
              source="chevron-right"
              color={theme.colors.outline}
              size={24}
            />
          </View>
        </Link>
      )}
    </View>
  );
}

function OutlineText({ text }: { text: string }) {
  const tailwind = useTailwind();
  const theme = useTheme();

  return (
    <Text
      variant="labelMedium"
      style={[{ color: theme.colors.outline }, tailwind("font-semibold")]}
    >
      {text}
    </Text>
  );
}
